import React, { useMemo, useEffect } from "react";
import CheckBox from "../CheckBox";

/**
 * @param {string} label - The label of the parent checkbox (e.g., "Extra Olives")
 * @param {Array} options - List of variant checkboxes [{ label: "for 8\" pizza", checked: true }, ...]
 * @param {boolean} showParentCheckbox - Whether to show the parent checkbox at all
 * @param {number} min - Minimum number of sub-options required to be selected (for validation)
 * @param {number} max - Maximum number of sub-options allowed to be selected
 * @param {function} onChange - Callback({ parentChecked, children }) fired whenever any checkbox changes
 */
function CheckBoxGroup({
  label,
  options = [],
  showParentCheckbox = true,
  min = 0,
  max = Infinity,
  onChange = () => {},
}) {
  const [childrenState, setChildrenState] = React.useState(options);
  //Sync internal state when options prop changes
  useEffect(() => {
    setChildrenState(options);
  }, [options]);
  const parentChecked = useMemo(
    () => childrenState.some((opt) => opt.checked),
    [childrenState]
  );

  const handleParentChange = () => {
    const newChecked = !parentChecked;
    const updated = childrenState.map((opt) => ({
      ...opt,
      checked: newChecked,
    }));
    setChildrenState(updated);
    onChange({ parentChecked: newChecked, children: updated });
  };

  const handleChildChange = (index) => {
    const selectedCount = childrenState.filter((c) => c.checked).length;
    const isCurrentlyChecked = childrenState[index].checked;

    // Guard max limit
    if (!isCurrentlyChecked && selectedCount >= max) return;

    const updated = [...childrenState];
    updated[index] = {
      ...updated[index],
      checked: !isCurrentlyChecked,
    };

    setChildrenState(updated);
    onChange({
      parentChecked: updated.some((c) => c.checked),
      children: updated,
    });
  };

  return (
    <div style={{ marginBottom: "1rem" }}>
      {showParentCheckbox ? (
        <CheckBox checked={parentChecked} onChange={handleParentChange}>
          {label}
        </CheckBox>
      ) : (
        <div style={{ fontWeight: 600 }}>{label}</div>
      )}

      <div style={{ paddingLeft: "1.5rem", display: "flex", flexDirection: "column", gap: "4px" }}>
        {childrenState.map((opt, i) => (
          <CheckBox
            key={i}
            checked={opt.checked}
            onChange={() => handleChildChange(i)}
          >
            {opt.label}
          </CheckBox>
        ))}
      </div>
    </div>
  );
}

export default CheckBoxGroup;